import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Download, Eye, FileText, Lock, Paperclip, UserMinus } from 'lucide-react';
import {
  Badge,
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
  Spinner,
} from '@/components/ui';
import {
  downloadCaseAttachment,
  downloadCaseExport,
  downloadWarningLetter,
  getCase,
  getCaseAccessLog,
  listCaseEntries,
  removeCaseMember,
  runCaseCommand,
  type HrCase,
  type HrCaseEntry,
} from '@/api/services/hrCases.service';
import { getErrorMessage } from '@/lib/getErrorMessage';
import {
  AddEntryForm,
  AddMemberForm,
  AppealDecisionForm,
  DecideForm,
  HearingForm,
  SuspensionForm,
  WarningLetterForm,
  WithdrawForm,
  type CaseAction,
} from './CaseActionForms';
import { GrievanceActionList, GrievanceActions, GrievanceSummary } from './GrievanceParts';
import { useCaseCommand } from './useCaseCommand';
import {
  accessActionText,
  APPEAL_LABELS,
  CASE_STATUS,
  DECISION_LABELS,
  describeEntry,
  ENTRY_LABELS,
  formatBytes,
  formatCaseDate,
  formatCaseDateTime,
  ROLE_LABELS,
  saveBlob,
  TERMINAL_STATUSES,
  warningLine,
} from './caseFormat';

function Field({ label, children }: { label: string; children: React.ReactNode }): React.ReactElement {
  return (
    <div>
      <dt className="text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400">
        {label}
      </dt>
      <dd className="mt-1 text-sm text-gray-900 dark:text-gray-100">{children}</dd>
    </div>
  );
}

function SectionHeading({
  children,
  action,
}: {
  children: React.ReactNode;
  action?: React.ReactNode;
}): React.ReactElement {
  return (
    <div className="mb-2 flex items-center justify-between gap-2">
      <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">{children}</h3>
      {action}
    </div>
  );
}

/** A command with no input, run from a single button. */
function CommandButton({
  companyId,
  hrCase,
  command,
  label,
  success,
}: {
  companyId: string;
  hrCase: HrCase;
  command: 'investigate' | 'close';
  label: string;
  success: string;
}): React.ReactElement {
  const run = useCaseCommand(
    companyId,
    hrCase._id,
    (requestId) => runCaseCommand(companyId, hrCase._id, { command, input: { requestId } }),
    success,
    () => undefined
  );
  return (
    <Button size="sm" onClick={() => run.submit(undefined)} loading={run.isPending}>
      {label}
    </Button>
  );
}

function RemoveMemberButton({
  companyId,
  hrCase,
  userId,
  name,
  onRemovedSelf,
}: {
  companyId: string;
  hrCase: HrCase;
  userId: string;
  name: string;
  onRemovedSelf: () => void;
}): React.ReactElement {
  const run = useCaseCommand(
    companyId,
    hrCase._id,
    (requestId) => removeCaseMember(companyId, hrCase._id, userId, { requestId }),
    'Removed from the case team',
    (result) => {
      if ('removed' in result) onRemovedSelf();
    }
  );
  return (
    <Button
      variant="ghost"
      size="sm"
      aria-label={`Remove ${name} from the case team`}
      onClick={() => run.submit(undefined)}
      loading={run.isPending}
    >
      <UserMinus className="h-4 w-4" aria-hidden />
    </Button>
  );
}

function EntryItem({
  companyId,
  caseId,
  entry,
}: {
  companyId: string;
  caseId: string;
  entry: HrCaseEntry;
}): React.ReactElement {
  const line = describeEntry(entry);
  const download = async (index: number, name: string): Promise<void> => {
    try {
      saveBlob(await downloadCaseAttachment(companyId, caseId, entry._id, index), name);
    } catch (error) {
      toast.error(getErrorMessage(error));
    }
  };
  return (
    <li className="space-y-1.5 p-3">
      <div className="flex flex-wrap items-center gap-2 text-xs text-gray-600 dark:text-gray-300">
        <span className="font-semibold uppercase tracking-wide text-gray-700 dark:text-gray-200">
          {ENTRY_LABELS[entry.type] ?? entry.type}
        </span>
        {entry.audience === 'parties' ? (
          <Badge variant="warning" size="sm">
            <Eye className="mr-1 h-3 w-3" aria-hidden />
            Shared with {entry.addressedTo?.name ?? 'the employee'}
          </Badge>
        ) : (
          <Badge variant="neutral" size="sm">
            <Lock className="mr-1 h-3 w-3" aria-hidden />
            Team only
          </Badge>
        )}
        <span>{entry.author?.name ?? 'Former user'}</span>
        <span aria-hidden>·</span>
        <time dateTime={entry.createdAt}>{formatCaseDateTime(entry.createdAt)}</time>
      </div>
      {line && <p className="text-sm text-gray-900 dark:text-gray-100">{line}</p>}
      {entry.meeting && (
        <p className="text-sm text-gray-900 dark:text-gray-100">
          {formatCaseDateTime(entry.meeting.at)}
          {entry.meeting.location ? ` · ${entry.meeting.location}` : ''}
        </p>
      )}
      {entry.body && (
        <p className="whitespace-pre-line break-words text-sm text-gray-800 dark:text-gray-200">
          {entry.body}
        </p>
      )}
      {entry.attachments.map((attachment) => (
        <Button
          key={attachment.index}
          variant="secondary"
          size="sm"
          onClick={() => void download(attachment.index, attachment.originalName)}
        >
          <Paperclip className="mr-1.5 h-4 w-4" aria-hidden />
          {attachment.originalName}
          <span className="ml-1.5 text-xs text-gray-500 dark:text-gray-400">
            {formatBytes(attachment.size)}
          </span>
        </Button>
      ))}
    </li>
  );
}

function AccessLog({ companyId, caseId }: { companyId: string; caseId: string }): React.ReactElement {
  const { data, isLoading, error } = useQuery({
    queryKey: ['hr-case', companyId, caseId, 'access-log'],
    queryFn: () => getCaseAccessLog(companyId, caseId),
    staleTime: 0,
  });
  if (isLoading) return <Spinner />;
  if (error) return <p className="text-sm text-danger">{getErrorMessage(error)}</p>;
  const rows = Array.isArray(data) ? data : [];
  return (
    <ol className="max-h-64 divide-y divide-gray-200 overflow-y-auto rounded-xl border border-gray-200 text-sm dark:divide-gray-700 dark:border-gray-700">
      {rows.map((row) => (
        <li key={row._id} className="flex flex-wrap gap-x-2 px-3 py-2">
          <span className="font-medium text-gray-900 dark:text-gray-100">
            {row.actor?.name ?? 'Former user'}
          </span>
          <span className="text-gray-700 dark:text-gray-200">
            {accessActionText(row)}
          </span>
          <time className="text-gray-500 dark:text-gray-400" dateTime={row.at}>
            {formatCaseDateTime(row.at)}
          </time>
        </li>
      ))}
    </ol>
  );
}

/**
 * One disciplinary case: summary, team, actions allowed by status and role,
 * and the append-only record. Owners and admins reading without joining the
 * team get a read-only view; every open of this dialog is logged server-side.
 */
export function CaseWorkspaceDialog({
  companyId,
  caseId,
  organizationWide,
  onClose,
}: {
  companyId: string;
  caseId: string;
  organizationWide: boolean;
  onClose: () => void;
}): React.ReactElement {
  const [action, setAction] = useState<CaseAction | null>(null);
  const [showAccessLog, setShowAccessLog] = useState(false);
  const queryClient = useQueryClient();
  const caseQuery = useQuery({
    queryKey: ['hr-case', companyId, caseId],
    queryFn: () => getCase(companyId, caseId),
  });
  const entriesQuery = useQuery({
    queryKey: ['hr-case', companyId, caseId, 'entries'],
    queryFn: () => listCaseEntries(companyId, caseId),
  });
  const hrCase = caseQuery.data;
  const done = (): void => setAction(null);

  let body: React.ReactNode;
  if (caseQuery.isLoading) {
    body = (
      <div className="flex justify-center py-10" role="status" aria-live="polite">
        <Spinner />
        <span className="sr-only">Loading case</span>
      </div>
    );
  } else if (caseQuery.error || !hrCase) {
    body = (
      <div className="space-y-3" role="alert">
        <p className="text-sm text-danger">
          {caseQuery.error ? getErrorMessage(caseQuery.error) : 'This case could not be displayed.'}
        </p>
        <Button variant="secondary" size="sm" onClick={() => void caseQuery.refetch()}>
          Retry
        </Button>
      </div>
    );
  } else {
    const isTeam = hrCase.viewerAccess === 'team';
    const grievance = hrCase.kind === 'grievance';
    const isDecider = hrCase.viewerRoles.includes('decision_maker');
    const hasDecider = hrCase.members.some(
      (member) => !member.removedAt && member.role === 'decision_maker'
    );
    const terminal = TERMINAL_STATUSES.includes(hrCase.status);
    const canDecide = ['investigating', 'hearing_scheduled'].includes(hrCase.status);
    const canSuspend =
      ['open', 'investigating', 'hearing_scheduled'].includes(hrCase.status) &&
      !hrCase.subjectSuspended;
    const isOfficer = hrCase.viewerRoles.includes('appeal_officer');
    const hasOfficer = hrCase.members.some(
      (member) => !member.removedAt && member.role === 'appeal_officer'
    );
    const decidedStage = ['decided', 'appeal_decided'].includes(hrCase.status);
    const letterActive = hrCase.letter && hrCase.letter.status !== 'voided';
    const currentMembers = hrCase.members.filter((member) => !member.removedAt);
    const entries = Array.isArray(entriesQuery.data) ? entriesQuery.data : [];
    const warning = warningLine(hrCase);
    // Every export is written to the case access log by the server.
    const exportRecord = async (): Promise<void> => {
      try {
        saveBlob(
          await downloadCaseExport(companyId, hrCase._id),
          `${hrCase.reference}-case-record.pdf`
        );
        await queryClient.invalidateQueries({
          queryKey: ['hr-case', companyId, hrCase._id, 'access-log'],
        });
        await queryClient.invalidateQueries({ queryKey: ['hr-cases-access-log'] });
      } catch (error) {
        toast.error(getErrorMessage(error));
      }
    };
    const openLetter = async (which: 'original' | 'completed'): Promise<void> => {
      try {
        saveBlob(
          await downloadWarningLetter(companyId, hrCase._id, which),
          `${hrCase.reference}-warning${which === 'completed' ? '-signed' : ''}.pdf`
        );
      } catch (error) {
        toast.error(getErrorMessage(error));
      }
    };

    body = (
      <div className="space-y-6 px-5 py-4">
        <div className="flex flex-wrap items-center gap-2">
          <Badge variant={CASE_STATUS[hrCase.status]?.variant ?? 'neutral'} dot>
            {CASE_STATUS[hrCase.status]?.label ?? hrCase.status}
          </Badge>
          <span className="text-sm text-gray-600 dark:text-gray-300">{hrCase.typeLabel}</span>
          <span className="font-mono text-xs text-gray-500 dark:text-gray-400">
            {hrCase.reference}
          </span>
          {hrCase.subjectSuspended && (
            <Badge variant="danger" size="sm">
              Employee suspended
            </Badge>
          )}
          <Button
            variant="ghost"
            size="sm"
            className="ml-auto"
            onClick={() => void exportRecord()}
          >
            <Download className="mr-1.5 h-4 w-4" aria-hidden />
            Export record
          </Button>
        </div>

        {!isTeam && (
          <p
            className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900 dark:border-amber-500/30 dark:bg-amber-500/10 dark:text-amber-200"
            role="note"
          >
            You are reading this case as an owner or admin. Your access is logged; join the case
            team to act on it.
          </p>
        )}

        {grievance && <GrievanceSummary hrCase={hrCase} />}
        {grievance && (
          <GrievanceActionList companyId={companyId} hrCase={hrCase} canAct={isTeam} />
        )}
        {isTeam && grievance && !terminal && (
          <GrievanceActions companyId={companyId} hrCase={hrCase} />
        )}

        {!grievance && (
        <dl className="grid gap-x-6 gap-y-4 sm:grid-cols-3">
          <Field label="Employee">
            {hrCase.subject?.name ?? '—'}
            {hrCase.subject?.employeeId && (
              <span className="block text-xs text-gray-600 dark:text-gray-300">
                {hrCase.subject.employeeId}
              </span>
            )}
          </Field>
          <Field label="Opened">
            {formatCaseDate(hrCase.openedAt)}
            {hrCase.openedBy && (
              <span className="block text-xs text-gray-600 dark:text-gray-300">
                by {hrCase.openedBy.name}
              </span>
            )}
          </Field>
          <Field label="Hearing">{formatCaseDateTime(hrCase.deadlines.hearingAt)}</Field>
          {hrCase.outcome?.decision && (
            <Field label="Decision">
              {DECISION_LABELS[hrCase.outcome.decision]}
              {warning && (
                <span className="block text-xs text-gray-600 dark:text-gray-300">{warning}</span>
              )}
            </Field>
          )}
          {hrCase.deadlines.appealClosesAt && !hrCase.appeal && (
            <Field label="Appeal window closes">
              {formatCaseDate(hrCase.deadlines.appealClosesAt)}
            </Field>
          )}
          {hrCase.appeal && (
            <Field label="Appeal">
              {hrCase.appeal.decision ? APPEAL_LABELS[hrCase.appeal.decision] : 'Awaiting decision'}
              <span className="block text-xs text-gray-600 dark:text-gray-300">
                Requested {formatCaseDate(hrCase.appeal.requestedAt)}
                {hrCase.appeal.decidedAt && ` · decided ${formatCaseDate(hrCase.appeal.decidedAt)}`}
              </span>
            </Field>
          )}
          {hrCase.letter && (
            <Field label="Warning letter">
              <span className="capitalize">{hrCase.letter.status.replaceAll('_', ' ')}</span>
              <span className="mt-1 flex flex-wrap gap-2">
                <Button variant="secondary" size="sm" onClick={() => void openLetter('original')}>
                  <FileText className="mr-1.5 h-4 w-4" aria-hidden />
                  Letter
                </Button>
                {hrCase.letter.status === 'completed' && (
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={() => void openLetter('completed')}
                  >
                    <Download className="mr-1.5 h-4 w-4" aria-hidden />
                    Signed copy
                  </Button>
                )}
              </span>
            </Field>
          )}
          {hrCase.withdrawnReason && (
            <div className="sm:col-span-3">
              <Field label="Withdrawn">{hrCase.withdrawnReason}</Field>
            </div>
          )}
        </dl>
        )}

        {isTeam && !grievance && (!terminal || hrCase.subjectSuspended) && (
          <section aria-label="Case actions" className="space-y-3">
            <div className="flex flex-wrap gap-2">
              {hrCase.status === 'open' && (
                <CommandButton
                  companyId={companyId}
                  hrCase={hrCase}
                  command="investigate"
                  label="Start investigation"
                  success="Investigation started"
                />
              )}
              {canDecide && (
                <Button size="sm" variant="secondary" onClick={() => setAction('hearing')}>
                  {hrCase.deadlines.hearingAt ? 'Reschedule hearing' : 'Schedule hearing'}
                </Button>
              )}
              {canDecide && isDecider && (
                <Button size="sm" onClick={() => setAction('decide')}>
                  Record decision
                </Button>
              )}
              {hrCase.status === 'appeal_requested' && isOfficer && (
                <Button size="sm" onClick={() => setAction('appeal')}>
                  Decide appeal
                </Button>
              )}
              {decidedStage && hrCase.outcome?.decision === 'warning' && !letterActive && (
                <Button size="sm" variant="secondary" onClick={() => setAction('letter')}>
                  Issue warning letter
                </Button>
              )}
              {decidedStage && !hrCase.appealOpen && (
                <CommandButton
                  companyId={companyId}
                  hrCase={hrCase}
                  command="close"
                  label="Close case"
                  success="Case closed"
                />
              )}
              {canSuspend && (
                <Button size="sm" variant="secondary" onClick={() => setAction('suspend')}>
                  Suspend employee
                </Button>
              )}
              {hrCase.subjectSuspended && (
                <Button size="sm" variant="secondary" onClick={() => setAction('reinstate')}>
                  Lift suspension
                </Button>
              )}
              {['open', 'investigating', 'hearing_scheduled'].includes(hrCase.status) && (
                <Button size="sm" variant="ghost" onClick={() => setAction('withdraw')}>
                  Withdraw
                </Button>
              )}
            </div>
            {hrCase.appealOpen && (
              <p className="text-xs text-gray-600 dark:text-gray-300">
                The employee can appeal until{' '}
                {formatCaseDate(hrCase.deadlines.appealClosesAt)}; the case can close after that.
              </p>
            )}
            {hrCase.status === 'appeal_requested' && !isOfficer && (
              <p className="text-xs text-gray-600 dark:text-gray-300">
                {hasOfficer
                  ? 'Only the case appeal officer can decide the appeal.'
                  : 'Add an appeal officer who was neither investigator nor decision maker.'}
              </p>
            )}
            {canDecide && !isDecider && (
              <p className="text-xs text-gray-600 dark:text-gray-300">
                {hasDecider
                  ? 'Only the case decision maker can record the decision.'
                  : 'Add a decision maker to the team before a decision is due.'}
              </p>
            )}
            {action === 'hearing' && (
              <HearingForm companyId={companyId} hrCase={hrCase} onDone={done} />
            )}
            {action === 'decide' && <DecideForm companyId={companyId} hrCase={hrCase} onDone={done} />}
            {action === 'withdraw' && (
              <WithdrawForm companyId={companyId} hrCase={hrCase} onDone={done} />
            )}
            {action === 'letter' && (
              <WarningLetterForm companyId={companyId} hrCase={hrCase} onDone={done} />
            )}
            {(action === 'suspend' || action === 'reinstate') && (
              <SuspensionForm
                key={action}
                companyId={companyId}
                hrCase={hrCase}
                onDone={done}
                mode={action}
              />
            )}
            {action === 'appeal' && (
              <AppealDecisionForm companyId={companyId} hrCase={hrCase} onDone={done} />
            )}
          </section>
        )}

        <section>
          <SectionHeading
            action={
              // A blocked grievance has no team yet; an owner or admin routes it.
              (isTeam || hrCase.status === 'blocked') &&
              !terminal &&
              action !== 'member' && (
                <Button variant="ghost" size="sm" onClick={() => setAction('member')}>
                  {hrCase.status === 'blocked' ? 'Add handler' : 'Add member'}
                </Button>
              )
            }
          >
            Case team
          </SectionHeading>
          <ul className="divide-y divide-gray-200 rounded-xl border border-gray-200 dark:divide-gray-700 dark:border-gray-700">
            {currentMembers.map((member) => (
              <li
                key={`${member.userId}-${member.role}`}
                className="flex items-center justify-between gap-2 px-3 py-2"
              >
                <span className="min-w-0">
                  <span className="block truncate text-sm font-medium text-gray-900 dark:text-gray-100">
                    {member.name}
                  </span>
                  <span className="text-xs text-gray-600 dark:text-gray-300">
                    {ROLE_LABELS[member.role]} · since {formatCaseDate(member.addedAt)}
                  </span>
                </span>
                {isTeam && !terminal && currentMembers.length > 1 && (
                  <RemoveMemberButton
                    companyId={companyId}
                    hrCase={hrCase}
                    userId={member.userId}
                    name={member.name}
                    onRemovedSelf={onClose}
                  />
                )}
              </li>
            ))}
          </ul>
          {action === 'member' && (
            <div className="mt-3">
              <AddMemberForm companyId={companyId} hrCase={hrCase} onDone={done} />
            </div>
          )}
        </section>

        <section>
          <SectionHeading
            action={
              isTeam &&
              !terminal &&
              action !== 'entry' && (
                <Button variant="ghost" size="sm" onClick={() => setAction('entry')}>
                  Add to record
                </Button>
              )
            }
          >
            Case record
          </SectionHeading>
          {action === 'entry' && (
            <div className="mb-3">
              <AddEntryForm companyId={companyId} hrCase={hrCase} onDone={done} />
            </div>
          )}
          {entriesQuery.isLoading ? (
            <Spinner />
          ) : entriesQuery.error ? (
            <p className="text-sm text-danger">{getErrorMessage(entriesQuery.error)}</p>
          ) : (
            <ol className="divide-y divide-gray-200 rounded-xl border border-gray-200 dark:divide-gray-700 dark:border-gray-700">
              {entries.map((entry) => (
                <EntryItem key={entry._id} companyId={companyId} caseId={hrCase._id} entry={entry} />
              ))}
            </ol>
          )}
        </section>

        {organizationWide && (
          <section>
            <SectionHeading
              action={
                <Button
                  variant="ghost"
                  size="sm"
                  aria-expanded={showAccessLog}
                  onClick={() => setShowAccessLog((value) => !value)}
                >
                  {showAccessLog ? 'Hide' : 'Show'}
                </Button>
              }
            >
              Access log
            </SectionHeading>
            {showAccessLog && <AccessLog companyId={companyId} caseId={hrCase._id} />}
          </section>
        )}
      </div>
    );
  }

  return (
    <Dialog
      open
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
    >
      <DialogContent className="max-h-[94dvh] w-[calc(100vw-1rem)] max-w-3xl overflow-y-auto p-0">
        <div className="border-b border-gray-200 px-5 py-4 pr-14 dark:border-gray-700">
          <DialogTitle>
            {hrCase?.kind === 'grievance'
              ? `Grievance from ${hrCase.complainant?.name ?? 'an employee'}`
              : (hrCase?.subject?.name ?? 'HR case')}
          </DialogTitle>
          <DialogDescription className="mt-1">
            {hrCase?.kind === 'grievance' ? 'Grievance' : 'Disciplinary case'} · confidential to
            the case team
          </DialogDescription>
        </div>
        {body}
      </DialogContent>
    </Dialog>
  );
}
